/**
 * Cómo suena tu bot (GKTIBO-122).
 *
 * El último turno de la entrevista, y el único que no es una pregunta abierta:
 * son **tres decisiones** —nombre, tono, largo— dentro de una sola tarjeta.
 *
 * ## Por qué una tarjeta y no cinco preguntas
 *
 * El PO pidió nombre, tono, largo «y todo eso». Puesto como cinco preguntas
 * seguidas, el alta pasa de cuatro a nueve y el dueño contesta cosas de estilo
 * antes de haber visto a su bot funcionar una sola vez — lo contrario de
 * «fácil de empezar», que es el objetivo del rediseño.
 *
 * ## Lo que hace que valga: se escucha
 *
 * «Cercano pero serio» no le dice nada a nadie hasta que lee una respuesta
 * escrita así. Y la muestra usa lo que él ya contestó, así que de paso le
 * prueba que las cuatro preguntas anteriores sirvieron para algo.
 *
 * **Si el modelo no se puede usar, se dice.** Una muestra fabricada por
 * nosotros haría que apruebe un tono que su bot no tiene.
 */

import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../../components/ui/Button';
import { api } from '../../lib/api.ts';
import { entrevista } from './textos-entrevista';

const t = entrevista.voz;

export interface Voz {
  nombre?: string;
  tono: string;
  largo: 'corto' | 'explica';
}

export interface ComoSuenaProps {
  tenantId: string;
  /** Cómo se llama el negocio. Se usa en «habla como …» y en la muestra. */
  negocio: string;
  guardando: boolean;
  /** Ya se guardó: la tarjeta deja de aceptar cambios. */
  resuelto?: boolean;
  onGuardar: (voz: Voz) => void;
}

/** Un par de botones que se excluyen, con el estado dicho para un lector. */
function Opcion({
  elegida,
  onClick,
  children,
}: {
  elegida: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      // `aria-pressed` y no sólo una clase: el color no se lee. Quien usa un
      // lector de pantalla tiene que poder saber cuál está elegida.
      aria-pressed={elegida}
      className={`voz-opcion${elegida ? ' es-elegida' : ''}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function ComoSuena({ tenantId, negocio, guardando, resuelto, onGuardar }: ComoSuenaProps) {
  /**
   * `null` = todavía no eligió.
   *
   * No arranca en «sin nombre» ni en «con nombre»: un default acá decide por
   * el dueño algo que el producto le está preguntando, y se lo lleva puesto
   * sin que lo note.
   */
  const [tieneNombre, setTieneNombre] = useState<boolean | null>(null);
  const [nombre, setNombre] = useState('');
  const [tono, setTono] = useState('');
  const [largo, setLargo] = useState<'corto' | 'explica'>('corto');
  const [cualEjemplo, setCualEjemplo] = useState(0);

  const vozActual = (): Voz => ({
    ...(tieneNombre && nombre.trim() ? { nombre: nombre.trim() } : {}),
    tono: tono.trim(),
    largo,
  });

  const muestra = useMutation({
    mutationFn: (indice: number) =>
      api.tenants.muestraDeLaVoz(tenantId, {
        ...vozActual(),
        pregunta: t.ejemplos[indice % t.ejemplos.length],
      }),
  });

  function escuchar(indice: number) {
    if (tono.trim().length === 0) return;
    setCualEjemplo(indice);
    muestra.mutate(indice);
  }

  // «No decidió» no es «sin nombre»: el comentario de arriba dice que no se
  // decide por el dueño, y `null` colándose como «sin nombre» era exactamente
  // eso. Y «se llama» sin nombre escrito tampoco es una respuesta.
  const nombreDecidido =
    tieneNombre === false || (tieneNombre === true && nombre.trim().length > 0);
  const puedeGuardar = nombreDecidido && tono.trim().length > 0 && !guardando && !resuelto;

  return (
    <div className="voz-tarjeta" data-testid="como-suena">
      <p className="voz-porque">{t.porque}</p>

      <fieldset className="voz-bloque">
        <legend>{t.tieneNombre}</legend>
        <div className="voz-opciones">
          <Opcion elegida={tieneNombre === false} onClick={() => setTieneNombre(false)}>
            {t.sinNombre(negocio)}
          </Opcion>
          <Opcion elegida={tieneNombre === true} onClick={() => setTieneNombre(true)}>
            {t.conNombre}
          </Opcion>
          {tieneNombre && (
            <>
              <label className="sr-only" htmlFor="voz-nombre">
                {t.etiquetaDelNombre}
              </label>
              <input
                id="voz-nombre"
                className="voz-nombre"
                type="text"
                value={nombre}
                aria-label={t.etiquetaDelNombre}
                onChange={(e) => setNombre(e.target.value)}
              />
            </>
          )}
        </div>
      </fieldset>

      <div className="voz-bloque">
        <label className="voz-etiqueta" htmlFor="voz-tono">
          {t.comoHabla}
        </label>
        <input
          id="voz-tono"
          className="voz-tono"
          type="text"
          value={tono}
          onChange={(e) => setTono(e.target.value)}
        />
        <div className="voz-atajos">
          <span className="voz-atajos-intro">{t.oTocaUno}</span>
          {t.atajos.map((a) => (
            <button
              key={a.id}
              type="button"
              className="voz-atajo"
              // RELLENA el campo, no lo reemplaza como valor fijo: lo que el
              // atajo puso pasa a ser texto del dueño, y él lo matiza.
              onClick={() => setTono(a.texto)}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>

      <fieldset className="voz-bloque">
        <legend>{t.cuantoExplica}</legend>
        <div className="voz-opciones">
          <Opcion elegida={largo === 'corto'} onClick={() => setLargo('corto')}>
            {t.alGrano}
          </Opcion>
          <Opcion elegida={largo === 'explica'} onClick={() => setLargo('explica')}>
            {t.explicaUnPoco}
          </Opcion>
        </div>
      </fieldset>

      <div className="voz-muestra">
        <div className="voz-muestra-encabezado">
          <span>{t.asiSuena}</span>
          {muestra.data && (
            <button
              type="button"
              className="voz-otro-ejemplo"
              onClick={() => escuchar(cualEjemplo + 1)}
            >
              {t.otroEjemplo}
            </button>
          )}
        </div>

        {!muestra.data && !muestra.isPending && (
          <Button
            type="button"
            variant="secondary"
            disabled={tono.trim().length === 0}
            onClick={() => escuchar(cualEjemplo)}
          >
            {t.escuchar}
          </Button>
        )}

        {muestra.isPending && <output className="voz-escuchando">{t.escuchando}</output>}

        {muestra.data && (
          <div className="voz-muestra-hilo">
            {/* La pregunta va a la vista: sin ella la respuesta no se puede
                juzgar, porque no se sabe a qué contesta. */}
            <p className="voz-muestra-pregunta">{t.ejemplos[cualEjemplo % t.ejemplos.length]}</p>
            <p className="voz-muestra-respuesta">
              {tieneNombre && nombre.trim() ? <strong>{nombre.trim()}: </strong> : null}
              {muestra.data.respuesta}
            </p>
          </div>
        )}

        {muestra.isError && (
          <p className="voz-error" role="alert">
            {t.noSePudoEscuchar}
          </p>
        )}
      </div>

      <div className="voz-pie">
        <span className="voz-se-cambia">{t.seCambia}</span>
        <Button type="button" disabled={!puedeGuardar} onClick={() => onGuardar(vozActual())}>
          {guardando ? t.guardando : t.guardar}
        </Button>
      </div>
    </div>
  );
}
